import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, Home, BarChart2, TrendingUp, Music2 } from 'lucide-react';
import { chartSources } from '../data/chartSources';
import { ThemeToggle } from './ThemeToggle';

const Navigation: React.FC = () => {
  const [isOpen, setIsOpen] = useState(false);
  const location = useLocation();

  const navigationItems = [
    { name: 'Home', href: '/', icon: Home },
    { name: 'Trending', href: '/trending', icon: TrendingUp },
    { name: 'Charts', href: '#', icon: BarChart2, isDropdown: true },
  ];

  const billboardCharts = chartSources.filter(source => source.id.startsWith('billboard'));

  return (
    <nav className="sticky top-0 z-50 glass-card shadow-sm">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between h-16">
          <div className="flex">
            <Link to="/" className="flex items-center space-x-2">
              <Music2 className="w-8 h-8 text-indigo-600 dark:text-indigo-400" />
              <span className="text-xl font-bold gradient-text">ChartFeeds</span>
            </Link>
          </div>

          {/* Desktop Navigation */}
          <div className="hidden sm:flex sm:items-center sm:space-x-1">
            {navigationItems.map((item) => (
              <div key={item.name} className="relative group">
                {item.isDropdown ? (
                  <div className="flex items-center cursor-pointer px-3 py-2 rounded-lg text-sm font-medium hover:bg-gray-100/50 dark:hover:bg-gray-700/50 transition-colors">
                    <item.icon className="w-5 h-5 mr-2 text-gray-500 dark:text-gray-400 group-hover:text-indigo-600 dark:group-hover:text-indigo-400" />
                    <span className="text-gray-700 dark:text-gray-200">{item.name}</span>
                  </div>
                ) : (
                  <Link
                    to={item.href}
                    className={`flex items-center px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                      location.pathname === item.href
                        ? 'bg-indigo-50 dark:bg-indigo-900/30 text-indigo-600 dark:text-indigo-400'
                        : 'text-gray-700 dark:text-gray-200 hover:bg-gray-100/50 dark:hover:bg-gray-700/50'
                    }`}
                  >
                    <item.icon className={`w-5 h-5 mr-2 ${
                      location.pathname === item.href
                        ? 'text-indigo-600 dark:text-indigo-400'
                        : 'text-gray-500 dark:text-gray-400'
                    }`} />
                    {item.name}
                  </Link>
                )}

                {item.isDropdown && (
                  <div className="hidden group-hover:block absolute z-10 -ml-4 mt-1 w-56">
                    <div className="py-1 glass-card rounded-lg shadow-lg ring-1 ring-black ring-opacity-5">
                      {billboardCharts.map((chart) => (
                        <Link
                          key={chart.id}
                          to={`/charts/${chart.id}`}
                          className="block px-4 py-2 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-100/50 dark:hover:bg-gray-700/50 transition-colors"
                        >
                          {chart.name}
                        </Link>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            ))}
            <div className="pl-4 ml-4 border-l border-gray-200/50 dark:border-gray-700/50">
              <ThemeToggle />
            </div>
          </div>

          {/* Mobile menu button and theme toggle */}
          <div className="sm:hidden flex items-center space-x-2">
            <ThemeToggle />
            <button
              onClick={() => setIsOpen(!isOpen)}
              className="glass-button p-2 rounded-lg"
              aria-label="Toggle menu"
            >
              {isOpen ? (
                <X className="w-5 h-5 text-gray-600 dark:text-gray-300" />
              ) : (
                <Menu className="w-5 h-5 text-gray-600 dark:text-gray-300" />
              )}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile menu */}
      <div className={`${isOpen ? 'block' : 'hidden'} sm:hidden border-t border-gray-200/50 dark:border-gray-700/50`}>
        <div className="pt-2 pb-3 space-y-1 px-4">
          {navigationItems.map((item) =>
            item.isDropdown ? (
              <div key={item.name} className="space-y-1">
                <div className="px-3 py-2 text-sm font-medium text-gray-600 dark:text-gray-300">
                  <div className="flex items-center">
                    <item.icon className="w-5 h-5 mr-2" />
                    {item.name}
                  </div>
                </div>
                <div className="pl-6 space-y-1">
                  {billboardCharts.map((chart) => (
                    <Link
                      key={chart.id}
                      to={`/charts/${chart.id}`}
                      className="block px-3 py-2 rounded-lg text-sm text-gray-500 dark:text-gray-400 hover:bg-gray-100/50 dark:hover:bg-gray-700/50"
                      onClick={() => setIsOpen(false)}
                    >
                      {chart.name}
                    </Link>
                  ))}
                </div>
              </div>
            ) : (
              <Link
                key={item.name}
                to={item.href}
                className={`block px-3 py-2 rounded-lg text-sm font-medium ${
                  location.pathname === item.href
                    ? 'bg-indigo-50 dark:bg-indigo-900/30 text-indigo-600 dark:text-indigo-400'
                    : 'text-gray-600 dark:text-gray-300 hover:bg-gray-100/50 dark:hover:bg-gray-700/50'
                }`}
                onClick={() => setIsOpen(false)}
              >
                <div className="flex items-center">
                  <item.icon className="w-5 h-5 mr-2" />
                  {item.name}
                </div>
              </Link>
            )
          )}
        </div>
      </div>
    </nav>
  );
};

export default Navigation;